/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Theme as MaterialTheme, useTheme, Paper, Stack, TextField, Button, Container, Typography, Card, CardContent, CardHeader, FormGroup, FormControlLabel, Switch, Box, Backdrop, CircularProgress } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import { useState, useEffect, Dispatch, SetStateAction, ChangeEvent, } from "react";
import MainLogo from "../../../common/images/digiacc.png"

import { useAppBarHeight, useDebounce } from '../../../common/CustomHooks';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useParams } from 'react-router-dom';
import OrganizationBusinessLogic from '../../../businessLogic/OrganizationBusinessLogic';
import { NumericFormatWhole } from '../../../common/CustomFunctions';
import { IOrganizationGuiModel } from '../../../model/IOrganizationGuiModel';

const Organization = (): JSX.Element => {

    const { organizationId } = useParams();

    const theme = useTheme();

    const { t, i18n } = useTranslation();

    const [loader, setLoader] = useState(false);

    const [name, setName] = useState<string | undefined>("");
    const [orgNumber, setOrgNumber] = useState<string | undefined>("");
    const [address1, setAddress1] = useState<string | undefined>("");
    const [address2, setAddress2] = useState<string | undefined>("");
    const [postalCode, setPostalCode] = useState<string | undefined>("");
    const [postArea, setPostArea] = useState<string | undefined>("");
    const [email, setEmail] = useState<string | undefined>("");
    const [webSite, setWebsite] = useState<string | undefined>("");
    const [phone, setPhone] = useState<string | undefined>("");
    const [fax, setFax] = useState<string | undefined>("");

    const [bankAccount, setBankAccount] = useState<string | undefined>("");
    const [iban, setIban] = useState<string | undefined>("");
    const [swift, setSwift] = useState<string | undefined>("");

    const [companyRegistered, setCompanyRegistered] = useState(false);
    const [canCollectVat, setCanCollectVat] = useState(false);

    //###############################################################################################################################################
    //########################################################API Calls and useEffects###############################################################
    useEffect(() => {
        let isMounted = true; //Avoid memory leaks. Async function running after dismount.
        //const service = new SourceDocumentService();

        getOrganizationData(isMounted);

        return () => { isMounted = false };
    }, [organizationId]);
    //###############################################################################################################################################
    //###############################################################################################################################################
    const getOrganizationData = async (isMounted:boolean) => {
        if (organizationId) {
            let logic = new OrganizationBusinessLogic();
            setLoader(true);
            const result = await logic.getOrganization(organizationId);
            setLoader(false);
            if (result.hasErrors) {
                if (isMounted) toast(t("basic.errorOrganizationData"));
            } else {
                if (result.value) {
                    if (isMounted) {
                        setName(result.value.name);
                        setOrgNumber(result.value.organizationNumber);
                        setAddress1(result.value.addressLine1);
                        setAddress2(result.value.addressLine2);
                        setPostalCode(result.value.postalCode);
                        setPostArea(result.value.postArea);
                        setEmail(result.value.email);
                        setWebsite(result.value.webSite);
                        setPhone(result.value.phone);
                        setFax(result.value.fax);

                        setBankAccount(result.value.bankAccount);
                        setIban(result.value.iban);
                        setSwift(result.value.swift);

                        setCompanyRegistered(result.value.companyRegistered);
                        setCanCollectVat(result.value.canCollectVAT);
                    }
                } else {
                    if (isMounted) toast(t("basic.errorOrganizationData"));
                }
                
            }
        }
    }
    const putOrganizationData = async () => {
        if (organizationId) {
            if (validateAccount()) {

                let logic = new OrganizationBusinessLogic();

                let data: IOrganizationGuiModel = {
                    id: organizationId,
                    name: name ? name : "",
                    customerNumber: undefined,
                    organizationNumber: organizationId,
                    addressLine1: address1,
                    addressLine2: address2,
                    postalCode: postalCode,
                    postArea: postArea,
                    bankAccount: bankAccount?.replaceAll(".",""),
                    iban: iban,
                    swift: swift,
                    companyRegistered: companyRegistered,
                    email: email,
                    webSite: webSite,
                    canCollectVAT: canCollectVat,
                    phone: phone,
                    fax: fax,
                }

                setLoader(true);
                const result = await logic.putOrganizationData(organizationId, data)
                setLoader(false);

                if (result.hasErrors) {
                    toast(t("basic.errorSavingOrganization") + result.errors.join(", "));
                } else {
                    toast(t("basic.dataSaved"));
                }

            } else {
                toast(t("basic.errorAccountInput"))
            }
        }
    }
    //###############################################################################################################################################
    //###############################################################################################################################################
    const appBarHeight = useAppBarHeight();

    function validateAccount():boolean {
        if (bankAccount && bankAccount !== "" && !/^\d+(?:\.\d+)*$/.test(bankAccount ? bankAccount : "")) {
            return false;
        } else {
            return true;
        }
    }
    //########################################################################################
    //########################################################################################
    return <main>
        <Grid container direction="column" wrap="nowrap"
            sx={{
                padding: theme.spacing(2),
                height: {
                    xs: "calc(100vh - " + appBarHeight + "px )",
                    xl: "calc(100vh)"
                },
                //"& .MuiDataGrid-columnHeaders": { backgroundColor: theme.palette.info.main, color: "white" },
                //"& .MuiDataGrid-footerContainer": { backgroundColor: theme.palette.info.main, color: "white" }
            }}>
            {/*<Grid container spacing={2} padding={theme.spacing(2)}>*/}

            {/*</Grid>*/}
            <Grid container paddingBottom={theme.spacing(2)}>
                <Grid xs={12}>
                    <Paper sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center", padding: theme.spacing(2)
                    }}>
                        <Typography variant="h6">{t("basic.organization")}</Typography>
                        <Button onClick={putOrganizationData} variant="contained">{t("basic.save")}</Button>
                    </Paper>
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid xs={12} md={6} xl={3}>
                    <Card sx={{ height: "100%" }}>
                        <CardHeader title={t("basic.organizationInfo")} titleTypographyProps={{ variant: "body1", fontWeight: "bold" }} />
                        <CardContent>
                            <Stack direction="column" spacing={4}>
                                <TextField
                                    fullWidth
                                    //css={textBoxInputStyleActor(theme)}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    size="small"
                                    variant="outlined"
                                    label={t("basic.name")}
                                    color="warning"
                                    value={name}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
                                <TextField
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    //css={textBoxInputStyleActor(theme)}
                                    disabled
                                    size="small"
                                    variant="outlined"
                                    label={t("basic.organizationNumber")}
                                    color="warning"
                                    value={orgNumber}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setOrgNumber(e.target.value)} />
                                <TextField
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    //css={textBoxInputStyleActor(theme)}
                                    size="small"
                                    variant="outlined"
                                    label={t("basic.addressLine1")}
                                    color="warning"
                                    value={address1}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setAddress1(e.target.value)} />
                                <TextField
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    //css={textBoxInputStyleActor(theme)}
                                    size="small"
                                    variant="outlined"
                                    label={t("basic.addressLine2")}
                                    color="warning"
                                    value={address2}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setAddress2(e.target.value)} />
                                <TextField
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    //css={textBoxInputStyleActor(theme)}
                                    size="small"
                                    variant="outlined"
                                    label={t("basic.postalCode")}
                                    color="warning"
                                    value={postalCode}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setPostalCode(e.target.value)} />
                                <TextField
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    //css={textBoxInputStyleActor(theme)}
                                    size="small"
                                    variant="outlined"
                                    label={t("basic.postArea")}
                                    color="warning"
                                    value={postArea}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setPostArea(e.target.value)} />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid xs={12} md={6} xl={3}>
                    <Card sx={{height:"100%"}}>
                        <CardHeader title={t("basic.contactInfo")} titleTypographyProps={{ variant: "body1", fontWeight: "bold" }} />
                        <CardContent>
                            <Stack direction="column" spacing={4}>
                                <TextField
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    //css={textBoxInputStyleActor(theme)}
                                    size="small"
                                    variant="outlined"
                                    label={t("basic.email")}
                                    color="warning"
                                    value={email}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} />
                                <TextField
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    //css={textBoxInputStyleActor(theme)}
                                    size="small"
                                    variant="outlined"
                                    label={t("basic.website")}
                                    color="warning"
                                    value={webSite}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setWebsite(e.target.value)} />
                                <TextField
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    //css={textBoxInputStyleActor(theme)}
                                    size="small"
                                    variant="outlined"
                                    label={t("basic.phone")}
                                    color="warning"
                                    value={phone}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)} />
                                <TextField
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    //css={textBoxInputStyleActor(theme)}
                                    size="small"
                                    variant="outlined"
                                    label={t("basic.fax")}
                                    color="warning"
                                    value={fax}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFax(e.target.value)} />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid xs={12} md={6} xl={3}>
                    <Card sx={{ height: "100%" }}>
                        <CardHeader title={t("basic.paymentInfo")} titleTypographyProps={{ variant: "body1", fontWeight: "bold" }} />
                        <CardContent>
                            <Stack direction="column" spacing={4}>
                                <TextField
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={!validateAccount()}
                                    //css={textBoxInputStyleActor(theme)}
                                    size="small"
                                    variant="outlined"
                                    label={t("basic.bankAccount")}
                                    color="warning"
                                    value={bankAccount}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setBankAccount(e.target.value)} />
                                <TextField
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    //css={textBoxInputStyleActor(theme)}
                                    size="small"
                                    variant="outlined"
                                    label={t("basic.IBAN")}
                                    color="warning"
                                    value={iban}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setIban(e.target.value)} />
                                <TextField
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    //css={textBoxInputStyleActor(theme)}
                                    size="small"
                                    variant="outlined"
                                    label={t("basic.SWIFT")}
                                    color="warning"
                                    value={swift}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSwift(e.target.value)} />
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={companyRegistered}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => setCompanyRegistered(e.target.checked)} />
                                        } label={t("basic.companyRegistered")} />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={canCollectVat}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => setCanCollectVat(e.target.checked)} />
                                        } label={t("basic.canCollectVat")} />
                                </FormGroup>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                {/*<Grid xs={12} md={6} xl={3}>*/}
                {/*    <Card sx={{ height: "100%" }}>*/}
                {/*        <CardHeader title={t("basic.logo")} titleTypographyProps={{ variant: "body1", fontWeight: "bold" }} />*/}
                {/*        <CardContent>*/}
                {/*            <Stack spacing={2}>*/}
                {/*                <Box*/}
                {/*                    component={Button}*/}
                {/*                    sx={{*/}
                {/*                        justifyContent: "center",*/}
                {/*                        alignItems: "center",*/}
                {/*                        height: "4rem",*/}
                {/*                        width: "100%",*/}
                {/*                        marginBottom: theme.spacing(10)*/}
                {/*                    }}>*/}
                {/*                    <img src={MainLogo} style={{ width: "100%", }} alt="Digital Accounting logo" />*/}
                {/*                </Box>*/}
                {/*                <TextField*/}
                {/*                    fullWidth*/}
                {/*                    size="small"*/}
                {/*                    variant="outlined"*/}
                {/*                    //label={t("basic.browse")}*/}
                {/*                    type="file"*/}
                {/*                    inputProps={{ accept:"image/png, image/gif, image/jpeg, image/jpg" }}*/}
                {/*                    color="warning"*/}
                {/*                    value={undefined}*/}
                {/*                    onChange={(e: ChangeEvent<HTMLInputElement>) => undefined} />*/}
                {/*                <Typography>{t("basic.supportedFileFormatsAre") + ": .jpeg, .jpg, .png, .gif"}</Typography>*/}
                {/*            </Stack>*/}
                {/*        </CardContent>*/}
                {/*    </Card>*/}
                {/*</Grid>*/}
            </Grid>
        </Grid>
        <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loader}
        >
            <CircularProgress color="inherit" />
        </Backdrop>
    </main>

}
export default Organization;